import { observer } from "mobx-react";
import { useCallback, useContext, useEffect, useRef } from "react";
import { xktViewerStore } from "./Stores/XKTViewerStore";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import { AttachXKTFiles } from "./Components/AttachXKTFiles";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { BcfSidebarContext } from "./Components/BCF/BcfSidebarContext";
import { XKTSearchForm } from "./Components/XKTSearchForm";
import BimViewerSdk from "@pmgnet/bim-viewer-sdk";
import { projectsDataStore } from "Stores/ProjectsStore";
import { userInfoStore } from "Stores";
import { ModelPositionModal } from "./Components/ModelPositionModal";

interface XKTViewerProps extends WithModal {
  isFullScreen?: boolean;
  fullMode?: boolean;
}

export const XKTViewer = withModal(
  observer(({ showModal, isFullScreen, fullMode }: XKTViewerProps) => {
    const { t } = useTranslation([I18N_NS.GENERAL, I18N_NS.XKT_VIEWER]);
    const iframeWrapperRef = useRef<HTMLDivElement>(null);
    const bimViewerSdk = useRef<BimViewerSdk | null>(null);

    const { showIssue, createIssue, addViewpointToBcfIssue, cancelAddViewpointMode, showFilterTopicsForm } =
      useContext(BcfSidebarContext);

    const handleShowAddModel = useCallback(() => {
      showModal(AttachXKTFiles, {
        modalProps: {
          title: t("Add models"),
          width: 800,
          footer: null,
          zIndex: 20001,
        },
        componentProps: {
          initialFolderId: xktViewerStore.parentFolderId,
          onAddFiles: xktViewerStore.addModel,
          fromXKTViewer: true,
        },
      });
    }, [showModal]);

    const handleShowSearchForm = useCallback(() => {
      showModal(XKTSearchForm, {
        componentProps: {
          searchStore: xktViewerStore.searchStore,
          models: xktViewerStore.models,
        },
        modalProps: {
          title: t("Search"),
          footer: null,
          zIndex: 10000,
          width: 700,
        },
      });
    }, [showModal]);

    const handleShowPositionModal = useCallback(
      (props) => {
        showModal(ModelPositionModal, {
          modalProps: {
            footer: null,
            zIndex: 10001,
            title: t("Change position"),
          },
          componentProps: {
            fileName: props.name,
            fileId: props.id,
            position: props.position,
            scale: props.scale,
            onChange: ({ fileName, position, scale, fileId }) => {
              xktViewerStore.updateModelPosition(fileName, position, scale, fileId);
            },
          },
        });
      },
      [showModal]
    );

    useEffect(() => {
      // Initialize the BimViewerSdk instance
      if (iframeWrapperRef.current && !bimViewerSdk.current) {
        bimViewerSdk.current = new BimViewerSdk({
          container: iframeWrapperRef.current,
          iframeUrl: import.meta.env.VITE_XKT_VIEWER_URL || "https://bim-viewer.master.plattform-test.pmgnet.de/",
          // iframeUrl: "http://localhost:5173/",
          autoInit: true,
          debug: true,
          locale: userInfoStore.language as "en" | "de",
          theme: {
            primaryBackground: "#fed330",
            primaryColor: "#313131",
            fontFamily: '"Roboto", sans-serif',
          },
          features: {
            bcf: projectsDataStore.bcfServer,
            bcfEnabled: !xktViewerStore.isXktFile,
            search: xktViewerStore.isSearchEnabled,
            addModel: true,
            canChangePosition: true,
          },
          originatingSystem: document.baseURI,
        });

        xktViewerStore.setSdk(bimViewerSdk.current);
        // Register UI handlers with the sdk
        bimViewerSdk.current.on("showAddModel", handleShowAddModel);
        bimViewerSdk.current.on("showBcfIssue", (payload) => showIssue(payload.data));
        bimViewerSdk.current.on("addBcfViewpoint", (payload) => addViewpointToBcfIssue(payload.viewpoint));
        bimViewerSdk.current.on("cancelAddBcfViewpointMode", cancelAddViewpointMode);
        bimViewerSdk.current.on("showBcfFilter", showFilterTopicsForm);
        bimViewerSdk.current.on("showCreateBcfIssue", createIssue);
        bimViewerSdk.current.on("showSearchForm", handleShowSearchForm);
        bimViewerSdk.current.on("showPositionModal", handleShowPositionModal);
      }
      return () => {
        if (bimViewerSdk.current) {
          bimViewerSdk.current.destroy();
          bimViewerSdk.current = null;
        }
      };
    }, [iframeWrapperRef]);

    return (
      <div id="bim-viewer" className="online-viewer--wrap" style={{ overflow: "hidden" }} ref={iframeWrapperRef}>
        {/* Iframe will be inserted by SDK here */}
      </div>
    );
  })
);
